<template>
  <div>
    <div class="auth-content">
      <div class="auth-context left">
        <span class="auth-title">{{ localize('auth.page.email.title') }}</span>
        <!-- <span class="auth-subtitle">{{ localize('auth.page.email.subtitle') }}</span> -->
        <!-- <span class="auth-subtitle">Receive a code to login or register.</span> -->
        <span class="auth-subtitle"></span>
      </div>

      <form @submit="handleSubmit">
        <gam-input v-bind="getEmailInput" v-model="form.email" />
      </form>

      <transition name="dropdown" mode="out-in">
        <GamButtonMessage v-if="errorMessage" v-bind="errorMessage" />
      </transition>

      <div class="auth-context policy"><span v-html="localize('auth.privacy')" /></div>
    </div>

    <div class="auth-methods-group">
      <div class="methods-row">
        <gam-button v-bind="getContinueButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterAuthType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import GamButton from '@/views/components/GamButton.vue';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamInput from '@/views/components/GamInput.vue';
import {
  GamButtonMessageVariant,
  GamButtonSize,
  GamButtonVariant,
} from '@/views/composables/constants/components/gamButton.constants';
import { GamInputName, GamInputValueType } from '@/views/composables/constants/components/gamInput.constants';
import type { GamButtonMessageType, GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamInputType } from '@/views/composables/models/components/GamInput';
import useValidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const router = useRouter();

const errorMessage = ref<GamButtonMessageType | null>(null);
const isLoading = ref<boolean>(false);
const form = reactive({
  email: '',
});
const validators = computed((): { email: any } => {
  return {
    email: { required },
  };
});
const v$ = useValidate(validators, form);

const route = useRoute();
watch(
  () => route.query.message,
  (message) => {
    if (String(message) === 'link_broken') {
      errorMessage.value = {
        label: 'The link you followed expired. Enter email again & try copy/pasting the code instead.',
        variant: GamButtonMessageVariant.ERROR,
      };

      router.replace({ ...route, query: { ...route.query, message: undefined } });
    }
  },
  { immediate: true },
);

const submitForm = async () => {
  isLoading.value = true;
  const result = await gambits.authService.authWithEmailUsername(form.email);
  if (result.data) {
    await router.push({ name: RouterAuthType.AUTH_OTP });
  } else if (result.error) {
    errorMessage.value = {
      label: result.error.message,
      variant: GamButtonMessageVariant.ERROR,
    };
  }
  isLoading.value = false;
};

const handleSubmit = (e: Event) => {
  e.preventDefault();
  // e.stopPropagation();

  submitForm();
};

const getEmailInput = computed((): GamInputType => {
  return {
    name: GamInputName.EMAIL_USERNAME,
    type: GamInputValueType.TEXT,
    placeholder: localize('auth.page.email.input.address.placeholder'),
    value: form.email,
    autocomplete: 'email',
    autofocus: true,
    isLowerCase: true,
  };
});

const getContinueButton = computed((): GamButtonType => {
  return {
    variant: GamButtonVariant.PRIMARY,
    size: GamButtonSize.LARGE,
    label: 'auth.page.email.continue.button.label',
    isFullSize: true,
    isCentered: true,
    isDisabled: v$.value.email.$invalid,
    isLoading: isLoading.value,
    message: errorMessage.value,
    onClick: submitForm,
  };
});
</script>

<style scoped lang="scss">
form {
  width: 100%;
}

.policy {
  margin-top: var(--spacing-md);
}
</style>
